import React from "react";
import lazyPage from "../../utils/lazyPage";
import { Navigate, useParams, useLocation } from "react-router-dom";
const AdminDashboard = lazyPage(() => import("../admin/AdminDashboard"));
const BillingPage = lazyPage(() => import("../admin/BillingPage"));
import TenantLoginPage from "../../pages/TenantLoginPage";
import { isMarketplaceHost, MARKETPLACE_HOST_SUBDOMAIN } from "../../utils/tenant";

import { isAdminLoginHandoff } from "../../utils/adminSession";

const ADMIN_ROLES = ['tenant_admin', 'admin', 'super_admin'];

/**
 * v4.17.4 — decides whether a stored login blob may enter the admin shell.
 *
 * tenant_admin/admin/super_admin always may. A ``tenant_user`` reaches
 * /admin ONLY as custom-role staff: the admin-login response stamps a
 * permissions payload / role identity onto the blob, whereas a roleless
 * legacy tenant_user or a customer-tab / magic-link session has none. This
 * mirrors the server admin-login gate (tenant_user requires a role_id) so a
 * non-staff session can't reach ungated Home/header calls by typing the URL.
 * Server-side require_permission is still the real enforcement.
 */
export const isAdminShellUser = (userData) => {
  if (!userData || typeof userData !== 'object') return false;
  if (ADMIN_ROLES.includes(userData.role)) return true;
  if (userData.role === 'tenant_user') {
    return !!(
      userData.is_system_admin ||
      userData.role_id ||
      userData.role_name ||
      (Array.isArray(userData.permissions) && userData.permissions.length > 0)
    );
  }
  return false;
};

/** Redirect helper that substitutes :storeSlug from URL params */
export const RedirectWithParams = ({ to }) => {
  const { storeSlug } = useParams();
  return <Navigate to={to.replace(':storeSlug', storeSlug)} replace />;
};

/** SuperAdmin routes — only accessible from main domain or preview */
export const SuperAdminRoute = ({ children }) => {
  const hostname = window.location.hostname;
  const isMainDomain = hostname === 'plateos.io' || hostname === 'localhost' || hostname === 'www.plateos.io';
  const isPreview = hostname.includes('preview.emergentagent.com') || hostname.includes('preview.emergent') || hostname.includes('emergent');
  if (!isMainDomain && !isPreview) {
    console.warn('SuperAdmin route accessed from tenant subdomain — redirecting');
    return <Navigate to="/" replace />;
  }
  return children;
};

// v4.9.1 — TenantCustomerRoute deleted: it was mounted nowhere and rendered the
// dead DynamicCustomerPortal chain (retired with the legacy storefront).

/** Smart /admin route — dashboard if admin logged in, login page otherwise */
export const TenantAdminRoute = ({ tenantSubdomain }) => {
  const location = useLocation();
  // Incoming credentials must be verified even if an old user blob remains.
  if (isAdminLoginHandoff(location.pathname, location.search)) {
    return <TenantLoginPage tenantSubdomain={tenantSubdomain} defaultTab="admin" />;
  }
  const token = localStorage.getItem('plateos_token');
  const user = localStorage.getItem('plateos_user');
  const hostname = window.location.hostname;
  const isPreviewOrDev = hostname.includes('preview.emergentagent.com') || hostname.includes('preview.emergent') || hostname.includes('localhost');

  let userData = null;
  let shouldRedirectToSuperAdmin = false;

  if (token && user) {
    try {
      userData = JSON.parse(user);
      if (isPreviewOrDev && userData.role === 'super_admin') {
        shouldRedirectToSuperAdmin = true;
      } else if (!isAdminShellUser(userData)) {
        // v4.17 — custom-role staff log in as ``tenant_user``; RBAC gates
        // what they see. Roleless tenant_user / customer sessions excluded.
        userData = null;
      }
    } catch { userData = null; }
  }

  if (shouldRedirectToSuperAdmin) return <Navigate to="/superadmin" replace />;
  if (userData) return <AdminDashboard tenantSubdomain={tenantSubdomain} />;
  return <TenantLoginPage tenantSubdomain={tenantSubdomain} defaultTab="admin" />;
};

/** /admin on main domain / preview — shows login or dashboard */
export const MainAdminRoute = () => {
  const location = useLocation();
  const token = localStorage.getItem('plateos_token');
  const user = localStorage.getItem('plateos_user');
  // Marketplace v1 (M1e) — on the marketplace host (marketplace.plateos.io) the
  // admin shell IS the 'marketplace' tenant, so both the dashboard and — the
  // real fix — the LOGIN must target that tenant. Without this the host falls
  // through to the bare main-domain path, whose TenantLoginPage carries NO
  // subdomain and posts the super-admin fallback ('plateos-super-admin'), so a
  // marketplace admin could never authenticate.
  const hostSubdomain = isMarketplaceHost() ? MARKETPLACE_HOST_SUBDOMAIN : undefined;
  if (isAdminLoginHandoff(location.pathname, location.search)) {
    return <TenantLoginPage tenantSubdomain={hostSubdomain} defaultTab="admin" />;
  }
  if (token && user) {
    try {
      const userData = JSON.parse(user);
      if (isAdminShellUser(userData)) {
        return <AdminDashboard tenantSubdomain={hostSubdomain} />;
      }
    } catch { /* invalid */ }
  }
  return <TenantLoginPage tenantSubdomain={hostSubdomain} defaultTab="admin" />;
};

/** Protected billing route — login if unauthenticated */
export const TenantBillingRoute = ({ tenantSubdomain }) => {
  const token = localStorage.getItem('plateos_token');
  const user = localStorage.getItem('plateos_user');
  if (token && user) {
    try {
      const userData = JSON.parse(user);
      if (isAdminShellUser(userData)) {
        return <BillingPage />;
      }
    } catch { /* invalid */ }
  }
  return <TenantLoginPage tenantSubdomain={tenantSubdomain} defaultTab="admin" />;
};
